<script setup lang="ts">
import { computed, ref } from 'vue'
import { Head, Link, router, useForm } from '@inertiajs/vue3'
import PageHeading from '@/Components/Headings/PageHeading.vue'
import Button from '@/Components/Utils/Button.vue'
import ReportEntryModal from '@/Components/Master/Reports/ReportEntryModal.vue'
import ReportTransactionsTable from '@/Components/Master/Reports/ReportTransactionsTable.vue'
import { PageHeading as PageHeadingTS } from '@/types/PageHeading'
import { useFormatCurrency } from '@/Composables/useFormatCurrency'
import { useFormatTime } from '@/Composables/useFormatTime'
import DatePicker from 'primevue/datepicker'
import InformationIcon from '@/Components/Utils/InformationIcon.vue'

type PaymentMethodOption = {
    label: string
    value: string
}

type EntryTypeOption = {
    label: string
    value: string
}

type Transaction = {
    id: string
    model_id: number | null
    source: 'invoice_payment' | 'manual'
    source_label: string
    entry_type: 'income' | 'expense'
    entry_type_label: string
    description: string
    amount: number
    payment_method: string
    payment_method_label: string
    transaction_date: string
    transaction_at: string
    created_by: string | null
    updated_by: string | null
    can_edit: boolean
    can_delete: boolean
    invoice: null | {
        reference: string
        slug: string
        created_at: string
        orders_count: number
        customer_name: string | null
    }
}

const props = defineProps<{
    pageHead?: PageHeadingTS
    title?: string
    filters: {
        start_date: string
        end_date: string
    }
    sales_overview: {
        invoice_payment_total: number
        invoice_payment_count: number
        invoice_count: number
        orders_count: number
    }
    cashflow_summary: {
        manual_income_total: number
        manual_expense_total: number
        total_income: number
        net_cashflow: number
        cash_available: number
    }
    payment_method_options: PaymentMethodOption[]
    entry_type_options: EntryTypeOption[]
    transactions: Transaction[]
    daily_income_summary: {
        date: string
        total: number
        methods: {
            payment_method: string
            payment_method_label: string
            amount: number
        }[]
    }[]
}>()

const toDateObject = (value?: string | null): Date | null => {
    if (!value) return null

    const [year, month, day] = value.split('-').map(Number)
    if (!year || !month || !day) return null

    return new Date(year, month - 1, day)
}

const toDateString = (value?: Date | Date[] | null): string | null => {
    if (!value || Array.isArray(value)) return null

    const year = value.getFullYear()
    const month = String(value.getMonth() + 1).padStart(2, '0')
    const day = String(value.getDate()).padStart(2, '0')

    return `${year}-${month}-${day}`
}

const isEntryModalVisible = ref(false)
const selectedTransaction = ref<TransactionRow | null>(null)
const isFiltering = ref(false)

const filterForm = useForm({
    start_date: props.filters.start_date,
    end_date: props.filters.end_date,
})

const filterStartDate = ref<Date | null>(toDateObject(props.filters.start_date))
const filterEndDate = ref<Date | null>(toDateObject(props.filters.end_date))

const applyFilters = () => {
    filterForm.start_date = toDateString(filterStartDate.value) ?? ''
    filterForm.end_date = toDateString(filterEndDate.value) ?? filterForm.start_date

    router.get(route('casheer.reports.index'), {
        start_date: filterForm.start_date || undefined,
        end_date: filterForm.end_date || undefined,
    }, {
        preserveScroll: true,
        preserveState: false,
        replace: true,
        onStart: () => {
            isFiltering.value = true
        },
        onFinish: () => {
            isFiltering.value = false
        },
    })
}

const openCreateModal = () => {
    selectedTransaction.value = null
    isEntryModalVisible.value = true
}

const startEdit = (transaction: Transaction) => {
    if (!transaction.can_edit || !transaction.model_id) {
        return
    }

    selectedTransaction.value = transaction
    isEntryModalVisible.value = true
}

const closeEntryModal = () => {
    isEntryModalVisible.value = false
    selectedTransaction.value = null
}

const deleteTransaction = (transaction: Transaction) => {
    if (!transaction.can_delete || !transaction.model_id) {
        return
    }

    if (!window.confirm(`Hapus ${transaction.entry_type_label.toLowerCase()} ini?`)) {
        return
    }

    router.delete(route('casheer.reports.delete', transaction.model_id), {
        preserveScroll: true,
        onSuccess: () => {
            if (selectedTransaction.value?.model_id === transaction.model_id) {
                closeEntryModal()
            }
        },
    })
}

const filterLabel = computed(() => {
    if (props.filters.start_date === props.filters.end_date) {
        return useFormatTime(props.filters.start_date, { formatTime: 'EEEE, dd MMM yyyy' })
    }

    return `${useFormatTime(props.filters.start_date, { formatTime: 'dd MMM yyyy' })} - ${useFormatTime(props.filters.end_date, { formatTime: 'dd MMM yyyy' })}`
})

const paymentMethodStyles: Record<string, { badge: string; button: string }> = {
    transfer: {
        badge: 'bg-sky-100 text-sky-700 ring-1 ring-sky-200',
        button: 'border-sky-300 bg-sky-50 text-sky-700',
    },
    cash: {
        badge: 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200',
        button: 'border-emerald-300 bg-emerald-50 text-emerald-700',
    },
    qris: {
        badge: 'bg-violet-100 text-violet-700 ring-1 ring-violet-200',
        button: 'border-violet-300 bg-violet-50 text-violet-700',
    },
    deposit: {
        badge: 'bg-amber-100 text-amber-700 ring-1 ring-amber-200',
        button: 'border-amber-300 bg-amber-50 text-amber-700',
    },
    other: {
        badge: 'bg-slate-100 text-slate-700 ring-1 ring-slate-200',
        button: 'border-slate-300 bg-slate-50 text-slate-700',
    },
}

const defaultPaymentMethodStyle = {
    badge: 'bg-gray-100 text-gray-700 ring-1 ring-gray-200',
    button: 'border-gray-300 bg-gray-50 text-gray-700',
}

const paymentMethodStyle = (method: string) => paymentMethodStyles[method] ?? defaultPaymentMethodStyle

const paymentMethodLabel = (method: string) =>
    props.payment_method_options.find((option) => option.value === method)?.label ?? method

const selectedPaymentMethod = ref<string | null>(null)

const filteredDailyIncomeSummary = computed(() => {
    if (!selectedPaymentMethod.value) {
        return props.daily_income_summary
    }

    return props.daily_income_summary
        .map((row) => {
            const methods = row.methods.filter((method) => method.payment_method === selectedPaymentMethod.value)
            const total = methods.reduce((sum, method) => sum + method.amount, 0)

            return { ...row, methods, total }
        })
        .filter((row) => row.total > 0)
})

const filteredGrandTotal = computed(() =>
    filteredDailyIncomeSummary.value.reduce((sum, row) => sum + row.total, 0),
)

const summaryCards = computed(() => [
    {
        label: 'Pembayaran dari Invoice',
        information: 'Semua pemasukan yang berasal dari pembayaran Invoice. Hanya pemasukan.',
        value: useFormatCurrency(props.sales_overview.invoice_payment_total),
        tone: 'text-sky-700 bg-sky-50 border-sky-200',
    },
    // {
    //     label: 'Transaksi Invoice',
    //     information: 'Banyaknya transaksi pada Invoice yang terjadi pada periode ini.',
    //     value: props.sales_overview.invoice_payment_count.toLocaleString('id-ID'),
    //     tone: 'text-slate-700 bg-slate-50 border-slate-200',
    // },
    {
        label: 'Banyaknya Invoice',
        information: 'Banyaknya Invoice berbeda yang ada pembayaran pada periode ini.',
        value: props.sales_overview.invoice_count.toLocaleString('id-ID'),
        tone: 'text-slate-700 bg-slate-50 border-slate-200',
    },
    // {
    //     label: 'Order Terkait',
    //     value: props.sales_overview.orders_count.toLocaleString('id-ID'),
    //     tone: 'text-slate-700 bg-slate-50 border-slate-200',
    // },
    // {
    //     label: 'Pemasukan Manual',
    //     value: useFormatCurrency(props.cashflow_summary.manual_income_total),
    //     tone: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    // },
    // {
    //     label: 'Pengeluaran Manual',
    //     value: useFormatCurrency(props.cashflow_summary.manual_expense_total),
    //     tone: 'text-rose-700 bg-rose-50 border-rose-200',
    // },
    {
        label: 'Arus Kas Bersih',
        information: 'Gabungan pembayaran invoice dan arus transaksi manual. Semua metode pembayaran. Kalkulasi pemasukan dan pengeluaran.',
        value: useFormatCurrency(props.cashflow_summary.net_cashflow),
        tone: props.cashflow_summary.net_cashflow >= 0
            ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
            : 'text-rose-700 bg-rose-50 border-rose-200',
    },
    {
        label: 'Tunai Tersedia',
        information: 'Jumlah uang tunai pada periode ini. Hanya metode pembayaran \'tunai\'. Kalkulasi pemasukan dan pengeluaran.',
        value: useFormatCurrency(props.cashflow_summary.cash_available),
        tone: props.cashflow_summary.cash_available >= 0
            ? 'text-slate-700 bg-slate-50 border-slate-200'
            : 'text-rose-700 bg-rose-50 border-rose-200',
    },
])
</script>

<template>
    <Head :title="title" />
    <PageHeading :data="pageHead" />
    <ReportEntryModal
        :visible="isEntryModalVisible"
        :transaction="selectedTransaction"
        :payment-method-options="payment_method_options"
        :entry-type-options="entry_type_options"
        :default-transaction-date="filters.end_date || filters.start_date"
        @update:visible="(value) => { if (value) isEntryModalVisible = true; else closeEntryModal() }"
    />

    <div class="px-4 py-6 sm:px-6 lg:px-8 space-y-6">
        <section class="rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
            <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <div>
                    <p class="text-sm font-medium text-gray-500">Periode aktif</p>
                    <h2 class="mt-1 text-lg font-semibold">{{ filterLabel }}</h2>
                    <p class="mt-1 text-sm text-gray-500">
                        Pembayaran invoice otomatis masuk sebagai pemasukan. Pemasukan/pengeluaran manual bisa diubah dari halaman ini.
                    </p>
                </div>

                <form class="grid grid-cols-1 gap-3 sm:grid-cols-3" @submit.prevent="applyFilters">
                    <label class="text-sm text-gray-600">
                        <span class="mb-1 block">Mulai</span>
                        <DatePicker
                            v-model="filterStartDate"
                            showIcon
                            fluid
                            dateFormat="dd/mm/yy"
                            inputClass="w-full"
                        />
                    </label>

                    <label class="text-sm text-gray-600">
                        <span class="mb-1 block">Sampai</span>
                        <DatePicker
                            v-model="filterEndDate"
                            showIcon
                            fluid
                            dateFormat="dd/mm/yy"
                            inputClass="w-full"
                            :minDate="filterStartDate || undefined"
                        />
                    </label>

                    <div class="flex items-end">
                        <Button
                            type="secondary"
                            label="Terapkan Filter"
                            :loading="isFiltering"
                            @click="applyFilters"
                        />
                    </div>
                </form>
            </div>
        </section>

        <section class="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
            <article
                v-for="card in summaryCards"
                :key="card.label"
                class="rounded-xl border p-4"
                :class="card.tone"
            >
                <div class="flex items-center justify-between">
                    <p class="text-sm font-medium opacity-80">{{ card.label }}</p>
                    <p v-if="card.information" class="text-sm">
                        <InformationIcon :information="card.information" />
                    </p>
                </div>
                <p class="mt-2 text-2xl font-semibold tracking-tight">{{ card.value }}</p>
            </article>
        </section>

        <section class="grid grid-cols-1 gap-6 xl:grid-cols-[1.1fr_1.5fr]">
            <div class="rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
                <div class="flex items-center justify-between gap-3">
                    <div>
                        <h3 class="text-base font-semibold">Pemasukan / Pengeluaran Manual</h3>
                        <p class="mt-1 text-sm text-gray-500">Kelola transaksi manual di luar pembayaran invoice lewat modal form.</p>
                    </div>

                    <Button type="create" label="Tambah Transaksi" @click="openCreateModal" />
                </div>
                <div class="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div class="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
                        <p class="text-sm font-medium text-emerald-800">Pemasukan Manual</p>
                        <p class="mt-1 text-xl font-semibold text-emerald-700">
                            {{ useFormatCurrency(cashflow_summary.manual_income_total) }}
                        </p>
                    </div>
                    <div class="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3">
                        <p class="text-sm font-medium text-rose-800">Pengeluaran Manual</p>
                        <p class="mt-1 text-xl font-semibold text-rose-700">
                            {{ useFormatCurrency(cashflow_summary.manual_expense_total) }}
                        </p>
                    </div>
                </div>
            </div>

            <div class="rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
                <div class="flex items-center justify-between gap-3">
                    <div>
                        <h3 class="text-base font-semibold">Rekap Pemasukan Perhari</h3>
                        <p class="mt-1 text-sm text-gray-500">
                            {{ selectedPaymentMethod
                                ? `Hanya metode ${paymentMethodLabel(selectedPaymentMethod)}.`
                                : 'Semua metode pembayaran.' }}
                        </p>
                    </div>
                </div>

                <div class="mt-4 flex flex-wrap gap-2">
                    <button
                        type="button"
                        class="rounded-full border px-3 py-1 text-xs font-medium"
                        :class="!selectedPaymentMethod
                            ? 'border-gray-800 bg-gray-800 text-white'
                            : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'"
                        @click="selectedPaymentMethod = null"
                    >
                        Semua
                    </button>
                    <button
                        v-for="option in payment_method_options"
                        :key="option.value"
                        type="button"
                        class="rounded-full border px-3 py-1 text-xs font-medium"
                        :class="selectedPaymentMethod === option.value
                            ? paymentMethodStyle(option.value).button
                            : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'"
                        @click="selectedPaymentMethod = option.value"
                    >
                        {{ option.label }}
                    </button>
                </div>

                <div v-if="filteredDailyIncomeSummary.length" class="mt-5 overflow-x-auto">
                    <table class="min-w-full divide-y divide-gray-200 text-sm">
                        <thead class="bg-gray-50 text-left text-gray-500">
                            <tr>
                                <th class="px-3 py-2 font-medium">Tanggal</th>
                                <th class="px-3 py-2 font-medium">Metode pembayaran</th>
                                <th class="px-3 py-2 font-medium text-right">Total</th>
                            </tr>
                        </thead>
                        <tbody class="divide-y divide-gray-100">
                            <tr v-for="row in filteredDailyIncomeSummary" :key="row.date">
                                <td class="px-3 py-3 align-top font-medium text-gray-700">
                                    {{ useFormatTime(row.date, { formatTime: 'EEEE, dd MMM yyyy' }) }}
                                </td>
                                <td class="px-3 py-3 align-top">
                                    <div class="flex flex-wrap gap-2">
                                        <span
                                            v-for="method in row.methods.filter((item) => item.amount > 0)"
                                            :key="method.payment_method"
                                            class="rounded-full px-2.5 py-1 text-xs font-medium"
                                            :class="paymentMethodStyle(method.payment_method).badge"
                                        >
                                            {{ method.payment_method_label }}: {{ useFormatCurrency(method.amount) }}
                                        </span>
                                    </div>
                                </td>
                                <td class="px-3 py-3 text-right font-semibold">
                                    {{ useFormatCurrency(row.total) }}
                                </td>
                            </tr>
                        </tbody>
                        <tfoot class="border-t-2 border-gray-200">
                            <tr>
                                <td class="px-3 py-3 font-semibold text-gray-700" colspan="2">
                                    Total {{ selectedPaymentMethod ? paymentMethodLabel(selectedPaymentMethod) : 'Semua Metode' }}
                                </td>
                                <td class="px-3 py-3 text-right font-semibold">
                                    {{ useFormatCurrency(filteredGrandTotal) }}
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                <div v-else class="mt-5 rounded-lg border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-500">
                    Belum ada pemasukan pada rentang tanggal ini.
                </div>
            </div>
        </section>

        <ReportTransactionsTable
            :transactions="transactions"
            :entry-type-options="entry_type_options"
            :payment-method-options="payment_method_options"
            @edit="startEdit"
            @delete="deleteTransaction"
        />
    </div>
</template>
